/**
 * Cross-module contracts for the Undercut web player core (M1).
 * Source of truth: plans/web-player/03-core-runtime.md and 09-resolution-and-layout.md.
 * Every module implements against these types; change them only together with all users.
 *
 * Units: "stage px" = layout px used by the style guides (1 stage px = f device px).
 * World = livetiming metres; world (x, y) maps to Babylon (x, 0, y).
 */
import type { Engine } from '@babylonjs/core/Engines/engine';
import type { Scene } from '@babylonjs/core/scene';
import type { Manifest, RaceEvent, LapsFile, ResultsFile, VectorsFile, FontMetrics, RadioFile, CommentaryFile } from './bundle/manifest.gen';
import type { NeutralApi } from './state/neutral.ts';
import type { TextSize } from './hud/type.ts';
import type { SheetApi, SheetState } from './layout/sheet.ts';
import type { Corner } from './hud/north-arrow.ts';
export type { Manifest, RaceEvent, LapsFile, ResultsFile, VectorsFile, FontMetrics, RadioFile, CommentaryFile };
export type { NeutralApi, NeutralKind, NeutralPeriod } from './state/neutral.ts';

// ───────────────────────────── geometry ─────────────────────────────
export interface Size { w: number; h: number }
export interface Rect { x: number; y: number; w: number; h: number }   // stage px, origin top-left, integers
export type RGB = readonly [number, number, number];
export type RGBA = readonly [number, number, number, number];

// ───────────────────────────── scale (09 §3) ─────────────────────────────
export interface ScaleState {
  /** device px per stage px (f = min(H/1080, W/1440)) */
  k: number;
  /** stage size in stage px */
  stage: Size;
  /** device px size of the stage element */
  device: Size;
  /** GL render scale r (backbuffer = device / r) */
  r: number;
  /** backbuffer px per stage px = k / r (shader uniform uPxScale) */
  pxScale: number;
}

// ───────────────────────────── bundle (03 §1) ─────────────────────────────
/**
 * Full-race decoded timeline (UCT2 chunks concatenated by the worker), physical units,
 * frame-major: value(frame, car) = arr[frame * nCars + car]. Frame f is at race t = f / hz.
 */
export interface Timeline {
  hz: 10; frames: number; nCars: number; lapM: number;
  dist: Float64Array;                 // m, cumulative race distance (exact to 1 mm)
  s: Float32Array;                    // m, derived: dist mod lapM (official s)
  lat: Float32Array;                  // m (+ = left of the reference line), lossless
  hdg: Float32Array;                  // rad [0, 2π)
  speed: Float32Array; thr: Float32Array; rpm: Float32Array;   // lossless vs race_state
  gap: Float32Array; int: Float32Array;                        // s, NaN preserved, lossless
  gear: Uint8Array; flags: Uint16Array; pos: Uint8Array; lapsdn: Uint8Array; lap: Uint8Array;
  cmp: Uint8Array; tyre: Uint8Array; stint: Uint8Array; pits: Uint8Array;
  latModel?: Float32Array;
}
export interface Bundle {
  baseUrl: string;
  manifest: Manifest;
  events: RaceEvent[];
  laps: LapsFile; results: ResultsFile;
  timeline: Timeline;
  warpRaceT: Float64Array;                      // Director race_t per video frame (8,429), f64 (timewarp.bin)
  warpSpeed: Float64Array;                      // Director speed per video frame (timewarp_speed.bin)
  track: { refXY: Float32Array; refS: Float32Array; refNrm: Float32Array };   // track/*.bin
  /** vectors.json; null when the loader skipped it (H2: styles that never show vectors) */
  vectors: VectorsFile | null;
  precompute<T>(name: string): Promise<T>;
  /** radio.json / commentary.json (race audio): fetched in the background after boot, never blocking it;
   *  resolve null when the race has none (or the fetch failed). Absent on hand-built test bundles. */
  radio?: Promise<RadioFile | null>;
  commentary?: Promise<CommentaryFile | null>;
  fonts: FontSet;
  /** timeline streaming (03 §1): true when stateAt(t) has every frame it reads; a miss prioritizes those chunks */
  ready(t: number): boolean;
  /** as ready() for every frame stateAt reads over [t0, t1] (trail windows) */
  readyRange(t0: number, t1: number): boolean;
  /** resolves when stateAt(t) can run (prioritizes its chunks); rejects only if one of them failed definitively */
  whenReady(t: number): Promise<void>;
  /** settles when every chunk is loaded (resolves) or loaded-or-failed (rejects); replaced by retry() */
  readonly complete: Promise<void>;
  /** re-request the chunks that failed; returns the new `complete` */
  retry(): Promise<void>;
  /** stop streaming, terminate the worker, release the timeline arrays */
  dispose(): void;
}
export interface LoadProgress { loaded: number; total: number; phase: string }

// ───────────────────────────── flags (02 timeline) ─────────────────────────────
/** Flag bits of the v6 timeline layout (bit i = 1 << i), read per race from
 *  `manifest.timeline.flag_bits` via `flagsOfManifest` at boot — the constant is the default
 *  (older manifests) and the boot asserts the bundle agrees (flagBitError). */
export const FLAG = { BRAKE: 1, DRS: 2, DRS_OPEN: 4, IN_PIT: 8, RETIRED: 16, FINISHED: 32, LAPPED: 64, HIDDEN: 128 } as const;
export type FlagSet = { -readonly [K in keyof typeof FLAG]: number };

/** FLAG bits derived from a manifest's flag_bits (bit positions). */
export function flagsOfManifest(man: Pick<Manifest, 'timeline'>): FlagSet {
  const b = (man.timeline as { flag_bits?: Manifest['timeline']['flag_bits'] }).flag_bits;
  if (!b) return { ...FLAG };
  return {
    BRAKE: 1 << b.brake, DRS: 1 << b.drs, DRS_OPEN: 1 << b.drs_open, IN_PIT: 1 << b.in_pit,
    RETIRED: 1 << b.retired, FINISHED: 1 << b.finished, LAPPED: 1 << b.lapped, HIDDEN: 1 << (b.hidden ?? 7),
  };
}

/** Boot check: null when the manifest's flag bits match FLAG (or are absent); else a fail-loud message. */
export function flagBitError(man: Pick<Manifest, 'timeline'>): string | null {
  const bits = (man.timeline as unknown as { flag_bits?: Record<string, number> }).flag_bits;
  if (!bits) return null;
  for (const [name, want] of Object.entries(FLAG)) {
    const pos = bits[name.toLowerCase()];
    if (pos === undefined) continue;
    if ((1 << pos) !== want) return `flag_bits changed: ${name.toLowerCase()} is bit ${String(pos)}, this player assumes bit ${String(Math.log2(want))} — update the player`;
  }
  return null;
}

/** Hard per-car capacity of the player (GL uniform arrays, scratch arrays, retired bitmask ≤ 32). */
export const MAX_CARS = 24;

// ───────────────────────────── state (03 §3) ─────────────────────────────
export interface FrameState {
  t: number; lap: number; leader: number; order: Uint8Array;
  // f64 where parity needs it: dist is ~3e5 m (f32 ulp 0.03 m); interpolated speed/thr/rpm/gap/int feed the
  // exact v6 strings (fmt_gap, int(rpm), int(round(speed))) and the 1e-6 fixture tolerances.
  x: Float32Array; y: Float32Array; hdg: Float32Array; s: Float32Array; dist: Float64Array; lat: Float32Array;
  speed: Float64Array; thr: Float64Array; rpm: Float64Array; gear: Uint8Array; flags: Uint16Array;
  pos: Uint8Array; gap: Float64Array; int: Float64Array; lapsdn: Uint8Array; lapNo: Uint8Array;
  cmp: Uint8Array; tyre: Uint8Array; stint: Uint8Array; pits: Uint8Array;
  latSource: 'data' | 'model';
}
export interface Track {
  length: number;                                               // official lap length (m)
  xy(s: number, out: Float32Array | number[]): void;            // official s → world xy on the reference line
  nrm(s: number, out: Float32Array | number[]): void;           // unit left normal
  tangent(s: number, out: Float32Array | number[]): void;
  cornerAt(s: number): { turn: number; name: string; short: string } | null;
  zoneAt(s: number): string;
}

// ───────────────────────────── time (03 §2, §10) ─────────────────────────────
export type Mode = { kind: 'director' } | { kind: 'free'; rate: 1 | 5 | 20 | 60 };
export interface ClockApi {
  readonly raceTime: number; readonly playing: boolean; readonly mode: Mode;
  readonly videoTime: number; readonly videoFrame: number; readonly effectiveRate: number;
  tick(dtWallSec: number): void;
  seek(raceTime: number): void;
  setPlaying(p: boolean): void;
  setMode(m: Mode): void;
  presentationAge(tEvent: number): number;
  presentationAgeFrames(tEvent: number): number;
  pulse(hz: number): number;                                    // 0..1 phase from presentation time
  /** Director parity rule (03 §10): race time of the current v6 frame (director), else raceTime; hold discrete values here */
  readonly frameRaceTime: number;
  /** race time nFrames presentation frames (30 fps) ago, for trail lengths */
  raceTimeAgo(nFrames: number): number;
  /** race seconds per presentation frame at the current rate (0 in holds) */
  racePerPresentationFrame(): number;
  on(ev: 'seek' | 'mode' | 'play', fn: () => void): () => void;
}

// ───────────────────────────── camera (03 §5, 09 §5) ─────────────────────────────
export interface ViewPreset {
  center: [number, number]; mpp: number; rotDeg: number;
  centerPx: [number, number];                                   // stage px
  mapRect: Rect;
  crisp?: boolean;                                              // default true; vector-only styles may opt out
  frameRect?: Rect;                                             // 09 §12: the rect the preset frames, when it differs from mapRect
}
export interface MapCameraApi {
  readonly mpp: number; readonly center: [number, number]; readonly rotDeg: number; readonly rect: Rect;
  readonly presetMpp: number;                                   // 09 §12: live fitted preset mpp; style zoom factors are relative to it
  setRect(r: Rect): void;
  setView(center: [number, number], mpp: number, rotDeg?: number): void;
  setPreset(p: ViewPreset): void;                               // the fitted preset for the current layout
  isAtPreset(): boolean;
  worldToStage(x: number, y: number, out: Float32Array | number[]): void;
  stageToWorld(px: number, py: number, out: Float32Array | number[]): void;
  flyTo(center: [number, number], mpp: number, durationSec: number): void;
  follow(car: number | null): void;
  readonly following: number | null;
  reset(): void;
  zoomAt(stagePx: number, stagePy: number, factor: number): void;
  panBy(dxStage: number, dyStage: number): void;
  relativeZoom(): number;                                       // mpp / preset mpp (for deep links, resize)
  on(ev: 'change' | 'idle', fn: () => void): () => void;
}

// ───────────────────────────── HUD (03 §8, 09 §7) ─────────────────────────────
export interface FontAtlas { metrics: FontMetrics; atlas: ImageBitmap }
export interface FontSet { '5x7': FontAtlas; '3x5': FontAtlas }
export type Cadence = 'frame' | 'onChange' | number;
export interface TextOpts {
  /** the text size: L, M or S (core/hud/type.ts); LOGO is the one non-tier size (title-card wordmark) */
  tier?: TextSize;
  anchor?: 'l' | 'c' | 'r'; shadow?: RGB | null; alpha?: number;
  cached?: boolean;                                             // false = glyph-by-glyph (fast-changing numbers); default true
}
export interface HudDraw {
  ctx: CanvasRenderingContext2D;                                // transform already set to stage px (×k)
  /** returns the width in stage px: glyph px · n / f, not an integer (round rects built from it up) */
  text(x: number, y: number, str: string, rgb: RGB, opts?: TextOpts): number;
  textWidth(str: string, opts?: TextOpts): number;
  /** glyph height of a tier in stage px (`font height · n / f`, not an integer) */
  textHeight(tier: TextSize): number;
  rect(x: number, y: number, w: number, h: number, rgb: RGB, alpha?: number): void;
  hline(x: number, y: number, w: number, rgb: RGB, alpha?: number): void;     // 1 stage px rule
  vline(x: number, y: number, h: number, rgb: RGB, alpha?: number): void;     // 1 stage px rule
  hole(x: number, y: number, w: number, h: number): void;                     // clear to transparent (map shows through)
  /** draw a stage-px canvas/bitmap (1 src px = 1 stage px) ×k, NEAREST (putImageData helper, 09 §12) */
  blit(src: CanvasImageSource & { width: number; height: number }, x: number, y: number, w?: number, h?: number): void;
}
export interface PanelSpec {
  id: string; rect: Rect; z?: number; cadence: Cadence;
  key?: () => string | number;                                  // redraw when it changes (onChange)
  draw(d: HudDraw, rect: Rect): void;
  /** static background, cached per panel at device resolution; rebuilt on resize / size change */
  background?(d: HudDraw, rect: Rect): void;
  clip?: boolean;                                               // default true
  /** paint bounds for clip: false (overflow area, absolute stage px); cleared/damaged with the panel */
  bounds?: Rect;
}
export interface HitRegion { id: string; rect: Rect; cursor?: string; onClick?(): void; onEnter?(): void; onLeave?(): void }
export interface HudApi {
  addPanel(p: PanelSpec): void;
  removePanel(id: string): void;
  setRect(id: string, r: Rect): void;
  invalidate(id?: string): void;
  /** Rebuild one panel's cached static background (e.g. the view's inset chrome: its cached
   *  background is empty while the inset is off, so the gate flip rebuilds it). */
  invalidateBackground(id: string): void;
  setHits(owner: string, regions: HitRegion[]): void;
  clear(): void;                                               // remove all panels and hits (unmount)
  flush(): void;                                               // draw dirty panels
}

// ───────────────────────────── style contract (03 §9, 09 §4) ─────────────────────────────
export type StyleId = 'undercut';
export interface StyleLayout {
  mapRect: Rect; view: ViewPreset;                              // view = fitted preset
  panels: Record<string, Rect>;
  insets?: Record<string, Rect>;
  inputRect: Rect; exclusions: Rect[];
  reserved: Rect[];
  clear: RGBA;
  isReference: boolean;                                         // stage is exactly 1920×1080 stage px
  page: RGB;                                                    // colour of margins / letterbox
  /** the preset `view` was fitted from, when it is not the style's `reference.view` (portrait) */
  fitRef?: ViewPreset;
  /** the zoom-out clamp in m per stage px for this layout (default `MPP_MAX`, portrait raises it) */
  mppMax?: number;
  /** portrait: the bottom sheet in its current state (stage px): the whole sheet, its handle (drag, tap to
   *  cycle) and the scrolling list under the tabs (zero in the peek); the shell lays its DOM layer over it */
  sheet?: { rect: Rect; grip: Rect; list: Rect; tops: Readonly<Record<SheetState, number>> };
}
/** Free-mode title-card policy (03 §10; 06-milestones owner decision #2 "show until first play, then
 *  skip"). The shell owns one per player, exposes it as `StyleContext.titleCard`; styles draw their
 *  own card and query it every frame. Director parity stays with the style (v6 `title` phase). */
export interface TitleCardPolicy {
  /** free mode: the card must show (t ≤ 0, paused, no play yet); false forever after the first play */
  visible(t: number, playing: boolean): boolean;
}

export interface StyleModule {
  id: StyleId;
  /** style key dispatch (03 §10): called by the shell for keys it does not handle itself (single
   *  characters only; modifier/editable-target keys never reach it). Return true when the style
   *  handled the key — the shell then swallows it (preventDefault). */
  onKey?(key: string): boolean;
  requires: { tiles?: string[]; precompute?: string[] };
  reference: { view: ViewPreset };
  /** Per-race parameters (M8): called by the shell with the loaded bundle BEFORE the first
   *  `layout()` of every mount (idempotent). Styles set their race view preset (`reference.view`),
   *  car count, laps, lap length… here, so `layout()` stays a pure function of the stage. Styles
   *  without it keep their built-in (Monza 2024) parameters. */
  setRace?(bundle: Bundle): void;
  /** `sheet`: the portrait bottom sheet's state (shell UI state, default 'peek'); `pip`: the viewer's
   *  battle-cam corner (shell UI state, r141; default the race preset's). Landscape ignores both */
  layout(stage: Size, sheet?: SheetState, pip?: Corner): StyleLayout;
  mount(ctx: StyleContext, layout: StyleLayout): Promise<void>;
  update(fs: FrameState, ctx: StyleContext): void;
  onSeek?(t: number, ctx: StyleContext): void;
  onCamera?(ctx: StyleContext): void;
  onLayout(layout: StyleLayout, ctx: StyleContext): void;
  unmount(ctx: StyleContext): void;
  /** The two cars of the battle the view is showing (the battle-cam pair), or null; the audio listener
   *  sits between them while the camera rests on its preset. */
  audioFocus?(): readonly [number, number] | null;
  /** linked event hover from the shell (scrub-bar pip hover, 03 §10); null ends it */
  onEventHover?(eventId: string | null, ctx: StyleContext): void;
}
export interface StyleContext {
  engine: Engine; scene: Scene; camera: MapCameraApi;
  bundle: Bundle; clock: ClockApi; track: Track;
  hud: HudApi; scale: ScaleState;
  follow: { get(): number | null; set(car: number | null): void };
  rng(seed: number): () => number;
  /**
   * Mark the GL picture dirty: the next frame renders even when paused and the camera is idle (the
   * shell renders on demand). Call it after a style-driven GL change (hover rings, follow rings…).
   */
  requestRender?(): void;
  /** GL helper factories (gl/*); typed in their modules */
  gl: unknown;
  /** shell-owned free-mode title-card policy (03 §10): styles show their card in free mode until the
   *  first play, then skip it (06 owner decision #2) */
  titleCard?: TitleCardPolicy;
  /** Neutralisations of this race (M8): `ctx.neutral.at(t)` is the active VSC/SC/red period
   *  (inclusive start, exclusive end) or null; `ctx.neutral.periods` is the full sorted list
   *  (empty when the race — e.g. Monza 2024 — has none, or the manifest predates the field). */
  neutral?: NeutralApi;
  /** Closed captions (race audio): shell-owned, mutated in place each frame. The view draws them only
   *  while `on`; absent on hand-built contexts (= no captions). */
  captions?: CaptionApi;
  /** LITE (r14): the rung in effect (0 off, 1 30 fps, 2 + r to dpr, 3 + softer battle cam), mutated in place by
   *  the player. The view draws its chip and coarsens the inset tiles from it; absent = 0. */
  lite?: { rung: number };
  /** the portrait bottom sheet (r14, core/layout/sheet.ts): shell-owned UI state; absent on hand-built contexts */
  sheet?: SheetApi;
}

/** The radio caption on screen: a driver tag, our paraphrase and, rarely, a short verbatim quote. */
export interface RadioCaption {
  id: string; driver: string; rgb: RGB; text: string; quote: string | null;
  /** "TEAM RADIO · L20", or "TEAM RADIO · AROUND LAP 20" for a lap-granular race */
  label: string;
  /** sound is on, the clip has audio and it failed to load */
  audioUnavailable: boolean;
}
/** The synthetic commentary line on screen. */
export interface CommentaryCaption { id: string; text: string; voiceUnavailable: boolean }
export interface CaptionState { radio: RadioCaption | null; commentary: CommentaryCaption | null }
/** `on` is the CC toggle; `key` changes whenever what is drawn changes (the HUD redraw key). */
export interface CaptionApi { on: boolean; state: CaptionState; key: string }

// ───────────────────────────── shell store (04) ─────────────────────────────
export interface AppState {
  race?: string | undefined;
  raceTime: number; playing: boolean; mode: Mode; follow: number | null;
}
